import { WorkPrompt, ProjectChecklist } from '@/components/Learn/WorkLessonTools';
import React, { useMemo, useState } from 'react';
import { View, Pressable, Platform } from 'react-native';
import { useRouter, usePathname } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useLanding } from '@/components/Landing/LandingKit';
import { DocsTopicLink } from '@/components/Docs/DocsDesign';
import {
  DocsParagraph,
  DocsDivider,
  CodeBlock,
  TipBlock,
  QuickLinkCard,
  StepList,
  Step,
} from '@/components/Docs/DocsComponents';
import { DocsPageOverview } from '@/components/Docs/DocsPageOverview';
import BrowserExtensionInstall from '@/components/Guides/BrowserExtensionInstall';
import DocsVisual, { DocsWorkflow } from '@/components/Docs/DocsVisual';
import { TutorialVideos } from '@/components/Tutorials/TutorialVideo';
import { getDocsNextStop } from '@/lib/tutorialVideos';
import {
  MockComposer,
  PromptExample,
  LessonQuiz,
  ProgressBar,
  KeyTakeaways,
  TryGabCTA,
} from '@/components/Learn/LearnComponents';

const slugify = (str) =>
  String(str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

// Block types that always render full-width and break a section group.
const STANDALONE_TYPES = new Set([
  'tutorial-videos',
  'product-visual',
  'product-workflow',
  'overview',
  'project-checklist',
  'progress-bar',
  'key-takeaways',
  'quiz',
  'try-gab-cta',
  'divider',
]);

// Content blocks whose `title` represents a section heading (the extractor
// folds single-block section titles onto the block itself). tip/code/etc.
// carry their own component title, so they are excluded.
const TITLED_SECTION_TYPES = new Set([
  'unordered-list',
  'ordered-list',
  'cards',
  'quick-links',
  'faq',
]);

const TIP_TYPES = new Set(['info', 'warning', 'tip', 'note']);

const splitParagraphs = (copy) =>
  String(copy || '')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

const itemText = (item) => {
  if (typeof item === 'string') return item;
  if (!item || typeof item !== 'object') return '';
  return item.title || item.label || item.description || item.copy || '';
};

// ============================================================================
// LIST (ordered / unordered with check, dash, dot, or per-item icon markers)
// ============================================================================
function GuideList({ block, accentColor = '#3B82F6' }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const ordered = block.type === 'ordered-list';
  const variant = block.variant || (ordered ? 'number' : 'dot');
  const items = Array.isArray(block.items) ? block.items : [];

  const markerBg = isDark ? 'rgba(52,211,153,0.15)' : 'rgba(52,211,153,0.12)';

  return (
    <View style={{ gap: 12, marginBottom: 20, marginTop: 4 }}>
      {items.map((item, i) => {
        const text = itemText(item);
        if (!text) return null;
        const icon = typeof item === 'object' ? item.icon : null;
        const isCheck = variant === 'check';

        let marker;
        if (ordered) {
          marker = (
            <View
              style={{
                width: 24,
                height: 24,
                borderRadius: 12,
                backgroundColor: `${accentColor}${isDark ? '26' : '1A'}`,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text tag="span" size="n" weight="bold" style={{ color: accentColor }}>
                {i + 1}
              </Text>
            </View>
          );
        } else if (icon) {
          marker = (
            <View
              style={{
                width: 24,
                height: 24,
                borderRadius: 8,
                backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={icon} size={12} color={colors.text_color_secondary} />
            </View>
          );
        } else if (isCheck) {
          marker = (
            <View
              style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                backgroundColor: markerBg,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="check" size={11} color="#34D399" solid />
            </View>
          );
        } else if (variant === 'dash') {
          marker = (
            <Text tag="span" size="l" weight="bold" color="secondary" style={{ width: 22, textAlign: 'center' }}>
              –
            </Text>
          );
        } else {
          marker = (
            <View style={{ width: 22, alignItems: 'center', justifyContent: 'center' }}>
              <View
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: colors.text_color_tertiary,
                }}
              />
            </View>
          );
        }

        return (
          <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
            <View style={{ marginTop: 2 }}>{marker}</View>
            <Text tag="p" size="l" style={{ flex: 1, lineHeight: 26 }}>
              {text}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

// ============================================================================
// CARDS — responsive grid of icon cards (title / subtitle / description)
// ============================================================================
function GuideCards({ block, accentColor = '#3B82F6' }) {
  const isDocs = /^\/(docs|learn)(\/|$)/.test(usePathname());
  const { theme } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const isDark = isDarkTheme(theme);
  const router = useRouter();
  const items = (Array.isArray(block.items) ? block.items : []).filter(
    (it) => it && typeof it === 'object',
  );
  if (!items.length) return null;

  if (isDocs) return <View style={{ marginBottom: 24 }}>{items.map((item, i) => item.href ? <DocsTopicLink key={i} href={item.href} title={item.title || item.label} description={[item.subtitle, item.description || item.copy].filter(Boolean).join('\n')} /> : <View key={i} style={{ paddingVertical: 18 }}><Text tag="h3" weight="semibold" style={{ fontSize: 18, lineHeight: 26 }}>{item.title || item.label}</Text>{item.subtitle ? <DocsParagraph>{item.subtitle}</DocsParagraph> : null}<DocsParagraph>{item.description || item.copy}</DocsParagraph></View>)}</View>;

  const basis = isXS ? '100%' : isSM ? 'calc(50% - 6px)' : 'calc(50% - 6px)';

  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 24, marginTop: 4 }}>
      {items.map((item, i) => {
        const color = item.color || accentColor;
        const href = item.href;
        const card = (hovered) => (
          <View
            style={{
              width: '100%',
              height: '100%',
              borderRadius: 16,
              padding: isXS ? 18 : 20,
              backgroundColor: hovered
                ? isDark
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.035)'
                : isDark
                  ? 'rgba(255,255,255,0.03)'
                  : 'rgba(0,0,0,0.02)',
              borderWidth: 1,
              borderColor: hovered
                ? isDark
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(0,0,0,0.1)'
                : isDark
                  ? 'rgba(255,255,255,0.06)'
                  : 'rgba(0,0,0,0.06)',
              gap: 6,
              transition: 'all 0.15s ease',
            }}
          >
            {!!item.icon && (
              <View
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  backgroundColor: `${color}15`,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 8,
                }}
              >
                <Icon name={item.icon} size={18} color={color} solid />
              </View>
            )}
            {!!item.title && (
              <Text tag="h3" size="l" weight="semibold">
                {item.title}
              </Text>
            )}
            {!!item.subtitle && (
              <Text tag="span" size="m" weight="medium" style={{ color, lineHeight: 20 }}>
                {item.subtitle}
              </Text>
            )}
            {!!(item.description || item.copy) && (
              <Text tag="p" size="m" color="secondary" style={{ lineHeight: 22 }}>
                {item.description || item.copy}
              </Text>
            )}
          </View>
        );

        return (
          <View key={i} style={{ flexBasis: basis, flexGrow: 0, flexShrink: 0 }}>
            {href ? (
              <Pressable
                onPress={() => router.push(href)}
                accessibilityRole="link"
                accessibilityLabel={item.title || item.label}
                style={{ height: '100%' }}
              >
                {({ hovered }) => card(hovered)}
              </Pressable>
            ) : (
              card(false)
            )}
          </View>
        );
      })}
    </View>
  );
}

// ============================================================================
// QUICK LINKS — row of QuickLinkCard
// ============================================================================
function GuideQuickLinks({ block, accentColor = '#3B82F6' }) {
  const isDocs = /^\/(docs|learn)(\/|$)/.test(usePathname());
  const { isXS } = useResponsiveness();
  const items = (Array.isArray(block.items) ? block.items : []).filter((it) => it && it.href);
  if (!items.length) return null;
  if (isDocs) return <View style={{ marginBottom: 24 }}>{items.map((item, i) => <DocsTopicLink key={i} href={item.href} title={item.title || item.label} description={item.description} />)}</View>;

  return (
    <View style={{ flexDirection: isXS ? 'column' : 'row', flexWrap: 'wrap', gap: 12, marginBottom: 24, marginTop: 4 }}>
      {items.map((item, i) => (
        <QuickLinkCard
          key={i}
          icon={item.icon || 'book-open'}
          title={item.title || item.label}
          description={item.description}
          href={item.href}
          color={item.color || accentColor}
        />
      ))}
    </View>
  );
}

// ============================================================================
// FAQ — collapsible Q/A accordion (first item starts open)
// ============================================================================
function GuideFaqItem({ question, answer, defaultOpen, accentColor = '#3B82F6' }) {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const [open, setOpen] = useState(!!defaultOpen);

  return (
    <View
      style={{
        borderWidth: 1,
        borderColor: open
          ? isDark
            ? `${accentColor}40`
            : `${accentColor}2E`
          : isDark
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.08)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      <Pressable
        onPress={() => setOpen((v) => !v)}
        accessibilityRole="button"
        accessibilityLabel={question}
        accessibilityState={{ expanded: open }}
        style={({ hovered }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          padding: 18,
          backgroundColor: hovered
            ? isDark
              ? 'rgba(255,255,255,0.03)'
              : 'rgba(0,0,0,0.015)'
            : 'transparent',
          transition: 'background-color 0.15s ease',
        })}
      >
        <Icon name="circle-question" size={16} color={accentColor} solid />
        <Text tag="h3" size="l" weight="semibold" style={{ flex: 1, lineHeight: 24 }}>
          {question}
        </Text>
        <Icon
          name="chevron-down"
          size={13}
          color={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)'}
          style={{
            transform: [{ rotate: open ? '180deg' : '0deg' }],
            transition: 'transform 0.2s ease',
          }}
        />
      </Pressable>
      {open && !!answer && (
        <View style={{ paddingHorizontal: 18, paddingBottom: 18, paddingLeft: 44 }}>
          <Text tag="p" size="m" color="secondary" style={{ lineHeight: 24 }}>
            {answer}
          </Text>
        </View>
      )}
    </View>
  );
}

function GuideFaq({ block, accentColor = '#3B82F6' }) {
  const items = Array.isArray(block.items) ? block.items : [];
  if (!items.length) return null;

  return (
    <View style={{ gap: 12, marginBottom: 24, marginTop: 4 }}>
      {items.map((item, i) => {
        const q = item.question || item.q || item.title;
        const a = item.answer || item.a || item.description || item.copy;
        if (!q) return null;
        return (
          <GuideFaqItem
            key={i}
            question={q}
            answer={a}
            defaultOpen={i === 0 && items.length <= 3}
            accentColor={accentColor}
          />
        );
      })}
    </View>
  );
}

// ============================================================================
// SECTION GROUP — heading (+ optional icon) + copy + nested content blocks.
// Mirrors DocsSection's DOM (nativeID + data-section-title) so the right-rail
// DocsTOC scanner picks it up.
// ============================================================================
function SectionGroup({ header, children, isFirst, accentColor = '#3B82F6' }) {
  const { isXS } = useResponsiveness();
  const title = header.title;
  const sectionId = title ? slugify(title) : undefined;
  const paragraphs = splitParagraphs(header.copy);

  return (
    <View
      nativeID={sectionId}
      style={{ marginBottom: isXS ? 40 : 56, marginTop: isFirst ? 0 : 4 }}
    >
      {!!title && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: isXS ? 14 : 18 }}>
          {!!header.icon && (
            <View
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                backgroundColor: `${accentColor}1F`,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={header.icon} size={16} color={accentColor} solid />
            </View>
          )}
          <Text
            tag="h2"
            weight="semibold"
            style={{ fontSize: isXS ? 24 : 28, letterSpacing: -0.5, flex: 1 }}
            {...(Platform.OS === 'web' ? { 'data-section-title': 'true' } : {})}
          >
            {title}
          </Text>
        </View>
      )}
      {paragraphs.map((p, i) => (
        <DocsParagraph key={i}>{p}</DocsParagraph>
      ))}
      {children}
    </View>
  );
}

// ============================================================================
// CONTENT BLOCK — renders a single non-section content block
// ============================================================================
function ContentBlock({ block, accentColor }) {
  switch (block.type) {
    case 'unordered-list':
    case 'ordered-list':
      return <GuideList block={block} accentColor={accentColor} />;
    case 'cards':
      return <GuideCards block={block} accentColor={accentColor} />;
    case 'quick-links':
      return <GuideQuickLinks block={block} accentColor={accentColor} />;
    case 'faq':
      return <GuideFaq block={block} accentColor={accentColor} />;
    case 'tip':
      return (
        <TipBlock type={TIP_TYPES.has(block.tipType) ? block.tipType : 'info'} title={block.title}>
          {block.copy}
        </TipBlock>
      );
    case 'work-prompt':
      return <WorkPrompt title={block.title} code={block.code} />;
    case 'code':
      return <CodeBlock code={block.code} language={block.language || 'text'} title={block.title} />;
    case 'steps': {
      const instructions = (
        <StepList>
          {(block.items || []).map((step, i) => (
            <Step key={i} title={step.title} color={accentColor}>
              {step.copy || step.description || ''}
            </Step>
          ))}
        </StepList>
      );
      return block.illustration === 'browser-extension-install' ? <BrowserExtensionInstall>{instructions}</BrowserExtensionInstall> : instructions;
    }
    case 'mock-composer':
      return (
        <MockComposer
          prompt={block.prompt}
          label={block.label}
          caption={block.caption}
          highlightParts={block.highlightParts || []}
        />
      );
    case 'prompt-example':
      return (
        <PromptExample
          bad={block.before}
          good={block.after}
          badCaption={block.badCaption}
          goodCaption={block.goodCaption}
        />
      );
    case 'divider':
      return <DocsDivider />;
    default:
      // Unknown content type with copy/title — render gracefully.
      if (block.copy || block.title) {
        return (
          <View style={{ marginBottom: 16 }}>
            {!!block.title && (
              <Text tag="h3" size="xl" weight="semibold" style={{ marginBottom: 6 }}>
                {block.title}
              </Text>
            )}
            {!!block.copy && <DocsParagraph>{block.copy}</DocsParagraph>}
          </View>
        );
      }
      return null;
  }
}

// ============================================================================
// STANDALONE BLOCK — full-width blocks rendered outside section grouping
// ============================================================================
function StandaloneBlock({ block }) {
  switch (block.type) {
    case 'tutorial-videos':
      return <TutorialVideos guideType={block.guideType} slug={block.slug} next={getDocsNextStop(block.slug)} />;
    case 'product-visual':
      return <DocsVisual visual={block.visual} priority={block.priority} />;
    case 'product-workflow':
      return <DocsWorkflow steps={block.steps} />;
    case 'project-checklist':
      return <ProjectChecklist title={block.title} items={block.items} />;
    case 'overview':
      return (
        <DocsPageOverview
          items={block.items || []}
          readTime={block.readTime}
          prerequisites={block.prerequisites || []}
        />
      );
    case 'progress-bar':
      return <ProgressBar current={block.current} total={block.total} labels={block.labels || []} />;
    case 'key-takeaways':
      return <KeyTakeaways items={block.items || []} />;
    case 'quiz':
      return <LessonQuiz questions={block.items || []} />;
    case 'try-gab-cta':
      return <TryGabCTA lessonTopic={block.lessonTopic} />;
    case 'divider':
      return <DocsDivider />;
    default:
      return null;
  }
}

// ============================================================================
// MAIN — groups the flat block array into section groups + standalone blocks
// ============================================================================
export default function GuidePageCopy({ blocks, guideType }) {
  const { c } = useLanding();
  const accentColor = c.accent;
  const units = useMemo(() => {
    const out = [];
    let group = null;
    const flush = () => {
      if (group) {
        out.push(group);
        group = null;
      }
    };

    for (const block of Array.isArray(blocks) ? blocks : []) {
      if (!block || typeof block !== 'object') continue;
      const type = block.type || 'section';
      const hasTitle = typeof block.title === 'string' && block.title.trim().length > 0;

      if (type === 'section') {
        flush();
        group = {
          kind: 'group',
          header: { title: block.title, icon: block.icon, copy: block.copy },
          children: [],
        };
      } else if (STANDALONE_TYPES.has(type)) {
        flush();
        out.push({ kind: 'standalone', block });
      } else if (TITLED_SECTION_TYPES.has(type) && hasTitle) {
        // A titled list/cards/faq block is its own section: render the heading,
        // then the block's body (its items) as the first child.
        flush();
        group = {
          kind: 'group',
          header: { title: block.title, icon: block.icon },
          children: [block],
        };
      } else if (group) {
        group.children.push(block);
      } else {
        // Content block with no preceding section heading — render bare.
        out.push({ kind: 'bare', block });
      }
    }
    flush();
    return out;
  }, [blocks]);

  if (!units.length) return null;

  let sectionSeen = false;

  return (
    <View>
      {units.map((unit, i) => {
        if (unit.kind === 'group') {
          const isFirst = !sectionSeen;
          sectionSeen = true;
          return (
            <SectionGroup key={i} header={unit.header} isFirst={isFirst} accentColor={accentColor}>
              {unit.children.map((child, ci) => (
                <ContentBlock key={ci} block={child} accentColor={accentColor} />
              ))}
            </SectionGroup>
          );
        }
        if (unit.kind === 'standalone') {
          return <StandaloneBlock key={i} block={unit.block} />;
        }
        return <ContentBlock key={i} block={unit.block} accentColor={accentColor} />;
      })}
    </View>
  );
}
